import { useCallback, useEffect, useState } from 'react'

const createSecureScript = (src, onLoad, onError) => {
  const script = document.createElement('script')
  script.src = src
  script.async = true
  script.defer = true
  script.onload = onLoad
  script.onerror = onError
  return script
}

export const useRecaptcha = () => {
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false)
  const [recaptchaError, setRecaptchaError] = useState(null)

  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY
  const RECAPTCHA_ENABLE =
    process.env.REACT_APP_RECAPTCHA_ENABLE === 'true' || false

  useEffect(() => {
    console.log('RECAPTCHA_ENABLE:', RECAPTCHA_ENABLE)
    if (!RECAPTCHA_ENABLE) {
      return
    }

    if (!RECAPTCHA_SITE_KEY) {
      setRecaptchaError('reCAPTCHA site key is not configured')
      return
    }

    // Check if reCAPTCHA is already loaded
    if (window.grecaptcha) {
      setIsRecaptchaLoaded(true)
      return
    }

    // Load reCAPTCHA script
    const loadRecaptchaScript = () => {
      try {
        const script = createSecureScript(
          `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`,
          () => {
            if (window.grecaptcha) {
              window.grecaptcha.ready(() => {
                setIsRecaptchaLoaded(true)
                setRecaptchaError(null)
              })
            }
          },
          () => {
            setRecaptchaError('Failed to load reCAPTCHA script')
          },
        )

        document.head.appendChild(script)
      } catch (error) {
        setRecaptchaError('Failed to generate reCAPTCHA script')
      }
    }

    loadRecaptchaScript()

    return () => {
      // Clean up script if component unmounts
      try {
        const existingScript = document.querySelector(
          `script[src*="recaptcha"]`,
        )
        if (existingScript && existingScript.parentNode) {
          existingScript.parentNode.removeChild(existingScript)
        }
      } catch (error) {
        console.warn('reCAPTCHA script cleanup warning:', error)
      }
    }
  }, [])

  const executeRecaptcha = useCallback(
    async (action) => {
      if (!RECAPTCHA_ENABLE) {
        return null
      }

      if (!RECAPTCHA_SITE_KEY) {
        setRecaptchaError('reCAPTCHA site key is not configured')
        return null
      }

      if (!isRecaptchaLoaded || !window.grecaptcha) {
        setRecaptchaError('reCAPTCHA is not loaded yet')
        return null
      }

      try {
        const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
          action,
        })
        setRecaptchaError(null)
        return token
      } catch (error) {
        console.error('reCAPTCHA execution failed:', error)
        setRecaptchaError('Failed to execute reCAPTCHA')
        return null
      }
    },
    [isRecaptchaLoaded],
  )

  return {
    executeRecaptcha,
    isRecaptchaLoaded,
    recaptchaError,
  }
}
